import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Chip, Divider, Grid, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import moment from 'moment';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getReservationOrderAction, getReservationOrderState } from '../../../../../../features/order/get_reservation_order';
import { getReservationOrderNotificationData } from '../../../../../../core/utils/notification_utils';
import { Button } from '../../../../../common/button';
import { MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../../../core/constants';
import { getOrderStatus, getOrderStatusColor, getPaymentType } from '../../../../../common/utils/order_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';

const ReservationOrderDialog = ({ reservation }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getReservationOrder = useSelector(getReservationOrderState);

    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (open) {
            dispatch(getReservationOrderAction({
                id: 'R' + reservation?.reservationOrderId,
                customerId: reservation?.customer?.customerId
            }))
                .then(response => {
                    showErrorMessage(getReservationOrderNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.REJECTED) onClose();
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, reservation?.customer?.customerId, reservation?.reservationOrderId, dispatch]);

    const onClose = () => setOpen(false);

    return (
        <>
            <Tooltip title="Szczegóły rachunku">
                <IconButton size="small" onClick={ () => setOpen(true) }>
                    <ReceiptIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="md">
                { getReservationOrder?.loading && <LinearProgress/> }

                { getReservationOrder?.data && (
                    <>
                        <DialogTitle>
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid>
                                    <Typography variant="h6" fontWeight="bold">
                                        Zamówienie { getReservationOrder?.data?.id }
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Data utworzenia
                                        zamówienia: <b>{ moment(getReservationOrder?.data?.createdDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }</b>
                                    </Typography>
                                </Grid>
                                <Chip
                                    size="medium"
                                    label={ getOrderStatus(getReservationOrder?.data?.statusName) }
                                    color={ getOrderStatusColor(getReservationOrder?.data?.statusName) }
                                    sx={ { fontWeight: 600 } }
                                />
                            </Grid>
                        </DialogTitle>
                        <Divider/>

                        <DialogContent sx={ { paddingTop: 2 } }>
                            <Grid container spacing={ 3 }>
                                <Grid item xs={ 12 }>
                                    <Typography variant="subtitle2" color="textSecondary"
                                                sx={ { textTransform: 'uppercase', fontWeight: 600, mb: 1 } }>
                                        Dane klienta
                                    </Typography>
                                    <Grid container spacing={ 1 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <Typography variant="body2">Imię i nazwisko:</Typography>
                                            <Typography variant="body1" fontWeight={ 500 }>
                                                { `${ reservation?.customer?.name || 'Brak' } ${ reservation?.customer?.surname || 'Brak' }` }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <Typography variant="body2">Telefon:</Typography>
                                            <Typography variant="body1" fontWeight={ 500 }>
                                                { `${ reservation?.customer?.phonePrefix || '' } ${ reservation?.customer?.phoneNumber || '' }` }
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={ 12 }><Divider/></Grid>

                                <Grid item xs={ 12 }>
                                    <Typography variant="subtitle2" color="textSecondary"
                                                sx={ { textTransform: 'uppercase', fontWeight: 600, mb: 1 } }>
                                        Szczegóły rachunku
                                    </Typography>
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <Typography variant="body2">Cena całkowita zapłacona:</Typography>
                                            <Typography variant="body1" fontWeight={ 600 } color="text.primary">
                                                { getReservationOrder?.data?.totalPrice.toFixed(2) } { getReservationOrder?.data?.currency }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <Typography variant="body2">Sposób płatności:</Typography>
                                            <Typography variant="body1" fontWeight={ 500 }>
                                                { getPaymentType(getReservationOrder?.data?.paymentType) }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <Typography variant="body2">Kwota dofinansowania:</Typography>
                                            <Typography variant="body1" fontWeight={ 500 }>
                                                { getReservationOrder?.data?.totalEmployerDiscountAmount.toFixed(2) } { getReservationOrder?.data?.currency }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <Typography variant="body2">Kwota z kuponów:</Typography>
                                            <Typography variant="body1" fontWeight={ 500 }>
                                                { getReservationOrder?.data?.totalCouponDiscountAmount.toFixed(2) } { getReservationOrder?.data?.currency }
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <Typography variant="body2">Naliczone punkty lojalnościowe:</Typography>
                                            <Typography variant="body1" fontWeight={ 500 }>
                                                { getReservationOrder?.data?.loyaltyPoint } pkt
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={ 12 }><Divider/></Grid>

                                <Grid item xs={ 12 }>
                                    <Typography variant="subtitle2" color="textSecondary"
                                                sx={ { textTransform: 'uppercase', fontWeight: 600, mb: 2 } }>
                                        Zamówione produkty
                                    </Typography>
                                    <TableContainer component={ Paper } variant="outlined">
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow
                                                    sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                                    <TableCell>Nazwa produktu</TableCell>
                                                    <TableCell align="center">Liczba szt.</TableCell>
                                                    <TableCell align="center">Data odbioru</TableCell>
                                                    <TableCell align="center">Produkt zwrócony</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                { getReservationOrder?.data?.products?.map((item) => (
                                                    <TableRow key={ item?.id } hover>
                                                        <TableCell style={ {
                                                            textDecoration: item?.isCancelled ? 'line-through' : 'none',
                                                            fontWeight: 500
                                                        } }>
                                                            { item?.productName }
                                                        </TableCell>
                                                        <TableCell align="center">{ item?.quantity }</TableCell>
                                                        <TableCell align="center">{ item?.pickupDate }</TableCell>
                                                        <TableCell align="center">
                                                            <Chip
                                                                label={ item?.isReturned ? "TAK" : "NIE" }
                                                                color={ item?.isReturned ? "warning" : "success" }
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                )) }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                ) }
                <DialogActions sx={ { p: 2, borderTop: theme => `1px solid ${ theme.palette.divider }` } }>
                    <Button onClick={ onClose } variant="outlined">Zamknij</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ReservationOrderDialog;